/**
 * Verifica si advancedSettings está habilitado
 * @param {Object} testGroupData - Datos del test group
 * @returns {boolean} - true si advancedSettings está disponible
 */
export const isAdvancedSettingsAvailable = (testGroupData) =>
  testGroupData?.advancedSetting?.available === true;

/**
 * Verifica si hay presets configurados
 * @param {Object} testGroupData - Datos del test group
 * @returns {boolean} - true si hay al menos un preset
 */
export const hasPresets = (testGroupData) => {
  const preSettings = testGroupData?.advancedSetting?.preSettings;
  return Array.isArray(preSettings) && preSettings.length > 0;
};

/**
 * Obtiene todos los presets configurados
 * @param {Object} testGroupData - Datos del test group
 * @returns {Array} - Array de presets (vacío si no hay)
 */
export const getPresets = (testGroupData) => testGroupData?.advancedSetting?.preSettings || [];

/**
 * Obtiene el preset marcado como default
 * @param {Object} testGroupData - Datos del test group
 * @returns {Object|undefined} - Preset default o undefined si no existe
 */
export const getDefaultPreset = (testGroupData) => {
  const presets = getPresets(testGroupData);
  return presets.find((preset) => preset?.isDefault);
};

/**
 * Obtiene todas las configuraciones de dificultad de todos los presets
 * @param {Object} testGroupData - Datos del test group
 * @returns {Array} - Array de configuraciones de dificultad
 */
export const getAllDifficultyConfigs = (testGroupData) => {
  const configs = [];
  const presets = getPresets(testGroupData);

  presets.forEach((preset) => {
    (preset?.difficultyConfig || []).forEach((config) => {
      configs.push(config);
    });
  });

  return configs;
};

/**
 * Verifica si se debe validar advancedSettings (está habilitado y tiene presets)
 * @param {Object} testGroupData - Datos del test group
 * @returns {boolean} - true si debe validar
 */
export const shouldValidateAdvancedSettings = (testGroupData) =>
  isAdvancedSettingsAvailable(testGroupData) && hasPresets(testGroupData);

/**
 * Calcula la duración total del preset default
 * @param {Object} testGroupData - Datos del test group
 * @returns {number} - Duración total en minutos (0 si no hay preset default)
 */
export const getDefaultPresetDuration = (testGroupData) => {
  const defaultPreset = getDefaultPreset(testGroupData);

  if (!defaultPreset?.difficultyConfig) {
    return 0;
  }

  return defaultPreset.difficultyConfig.reduce(
    (sum, config) => sum + (config?.quantity || 0) * (config?.duration || 0),
    0
  );
};

/**
 * Obtiene una configuración de dificultad específica de un preset
 * @param {Object} preset - Preset del que obtener la configuración
 * @param {string} difficultyId - ID de la dificultad
 * @param {string} theme - Tema (opcional)
 * @param {Array} subSkillIds - IDs de subskills (opcional)
 * @returns {Object|undefined} - Configuración encontrada o undefined
 */
export const getPresetConfig = (preset, difficultyId, theme = '', subSkillIds = []) => {
  if (!preset?.difficultyConfig) {
    return undefined;
  }

  return preset.difficultyConfig.find((config) => {
    const matchesDifficulty = config?.difficultyId === difficultyId;
    const matchesTheme = !theme || config?.theme === theme;
    const matchesSubskills =
      subSkillIds.length === 0 ||
      (config?.subSkillIds || []).some((id) => subSkillIds.includes(id));

    return matchesDifficulty && matchesTheme && matchesSubskills;
  });
};

/**
 * Valida si un preset tiene al menos una configuración de dificultad
 * @param {Object} preset - Preset a validar
 * @returns {boolean} - true si tiene al menos una configuración
 */
export const presetHasConfigs = (preset) => {
  const configs = preset?.difficultyConfig;
  return Array.isArray(configs) && configs.length > 0;
};

/**
 * Formatea las configuraciones de dificultad para validación
 * @param {Object} testGroupData - Datos del test group
 * @param {Array} testDifficulty - Array de dificultades disponibles
 * @returns {Array} - Array de configuraciones formateadas para validación
 */
export const formatDifficultyConfigsForValidation = (testGroupData, testDifficulty) => {
  const configs = [];
  const presets = getPresets(testGroupData);

  presets.forEach((preset) => {
    (preset?.difficultyConfig || []).forEach((config) => {
      configs.push({
        name: `${testDifficulty?.find((t) => t.id === config?.difficultyId)?.name}/${Boolean(
          config?.listeningTest
        )}/${config?.theme || ''}`,
        quantity: config?.quantity,
      });
    });
  });

  return configs;
};
